import { createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { formatTextForSentryLog } from 'src/utils/Formatter';
import { songsLibraryOverview } from './Library.thunks';
import { SongInsightsData } from '../Dashboard';
import {
  hasEveningInsight,
  hasGenresInsight,
  hasMomentumInsight,
  hasMorningInsight,
  hasNightInsight,
  hasRegionsInsight,
  hasRotationInsight,
} from '../SmartInsights/SmartInsightsCardContentHelper';

export interface Artist {
  id: string;
  name: string;
}

export interface TrackingPeriod {
  start_tracking_on: string;
  end_tracking_on: string;
  monitoring_end_on: string;
}

type TableStatus = {
  sortingOrder: 'ASC' | 'DESC';
  sortingId: string;
  songSortSelected: boolean;
};

export interface SongLibrary {
  id: string;
  title: string;
  artists: Artist[];
  tracking_periods: TrackingPeriod[];
  count: number;
  prev_count: number;
  difference: number;
  is_scheduled: boolean;
  all_count: number;
  insights: SongInsightsData;
  insightsFlags?: SongInsightFlags;
}

export type SongInsightFlags = {
  hasMomentumGainInsight: boolean;
  hasMomentumStagnantInsight: boolean;
  hasMomentumDownInsight: boolean;
  hasPeakAirplayMorningInsight: boolean;
  hasPeakAirplayEveningInsight: boolean;
  hasPeakAirplayNightInsight: boolean;
  hasRotationInsight: boolean;
  hasRegionsInsight: boolean;
  hasGenresInsight: boolean;
};

// Define a type for songsLibraryInsights with numbers for each has insight
export interface SongLibraryInsights {
  momentumInsightsGain: number;
  momentumInsightsStagnant: number;
  momentumInsightsDown: number;
  peakAirplayMorningInsights: number;
  peakAirplayEveningInsights: number;
  peakAirplayNightInsights: number;
  rotationInsights: number;
  regionsInsights: number;
  genresInsights: number;
}

export interface SongLibraryInsightsLists {
  momentumInsightsGain: SongLibrary[];
  momentumInsightsStagnant: SongLibrary[];
  momentumInsightsDown: SongLibrary[];
  peakAirplayMorningInsights: SongLibrary[];
  peakAirplayEveningInsights: SongLibrary[];
  peakAirplayNightInsights: SongLibrary[];
  rotationInsights: SongLibrary[];
  regionsInsights: Record<string, SongLibrary[]>;
  genresInsights: Record<string, SongLibrary[]>;
}

export interface SongsResponse {
  songsLibrary: SongLibrary[];
  songsLibraryInsights?: SongLibraryInsights;
  songsLibraryInsightLists?: SongLibraryInsightsLists;
  tableStatus: TableStatus;
  isFetching: boolean;
  hasError: boolean;
}

const initialTableStatus: TableStatus = {
  sortingOrder: 'ASC',
  sortingId: 'default',
  songSortSelected: true,
};

const Library = createSlice({
  name: 'AddTrack',
  initialState: {
    songsLibrary: [],
    songsLibraryInsights: {
      momentumInsightsGain: 0,
      momentumInsightsStagnant: 0,
      momentumInsightsDown: 0,
      peakAirplayMorningInsights: 0,
      peakAirplayEveningInsights: 0,
      peakAirplayNightInsights: 0,
      rotationInsights: 0,
      regionsInsights: 0,
      genresInsights: 0,
    },
    songsLibraryInsightLists: {
      momentumInsightsGain: [],
      momentumInsightsStagnant: [],
      momentumInsightsDown: [],
      peakAirplayMorningInsights: [],
      peakAirplayEveningInsights: [],
      peakAirplayNightInsights: [],
      rotationInsights: [],
      regionsInsights: {},
      genresInsights: {},
    },
    tableStatus: initialTableStatus,
    isFetching: true,
    hasError: false,
  } as SongsResponse,
  extraReducers: (builder) =>
    builder
      .addCase(songsLibraryOverview.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(songsLibraryOverview.fulfilled, (state, { payload }) => {
        const insightCounts: SongLibraryInsights = {
          momentumInsightsGain:
            payload.songsLibraryInsights?.momentumInsightsGain ?? 0,
          momentumInsightsStagnant:
            payload.songsLibraryInsights?.momentumInsightsStagnant ?? 0,
          momentumInsightsDown:
            payload.songsLibraryInsights?.momentumInsightsDown ?? 0,
          peakAirplayMorningInsights:
            payload.songsLibraryInsights?.peakAirplayMorningInsights ?? 0,
          peakAirplayEveningInsights:
            payload.songsLibraryInsights?.peakAirplayEveningInsights ?? 0,
          peakAirplayNightInsights:
            payload.songsLibraryInsights?.peakAirplayNightInsights ?? 0,
          rotationInsights: payload.songsLibraryInsights?.rotationInsights ?? 0,
          regionsInsights: payload.songsLibraryInsights?.regionsInsights ?? 0,
          genresInsights: payload.songsLibraryInsights?.genresInsights ?? 0,
        };

        const insightLists: SongLibraryInsightsLists = {
          momentumInsightsGain: [],
          momentumInsightsStagnant: [],
          momentumInsightsDown: [],
          peakAirplayMorningInsights: [],
          peakAirplayEveningInsights: [],
          peakAirplayNightInsights: [],
          rotationInsights: [],
          regionsInsights: {},
          genresInsights: {},
        };

        const songsWithInsightFlags: SongLibrary[] = payload.data.map(
          (song: SongLibrary) => {
            const hasMorning = hasMorningInsight(
              song.insights as SongInsightsData
            );
            const hasEvening = hasEveningInsight(
              song.insights as SongInsightsData
            );
            const hasNight = hasNightInsight(song.insights as SongInsightsData);
            const hasMomentum = hasMomentumInsight(
              song.insights as SongInsightsData
            );
            const momentumCategory =
              song.insights?.momentum?.category ?? 'stagnant';
            const hasMomentumGain =
              (hasMomentum && momentumCategory === 'positive') || false;
            const hasMomentumStagnant =
              (hasMomentum && momentumCategory === 'stagnant') || false;
            const hasMomentumDown =
              (hasMomentum && momentumCategory === 'negative') || false;
            const hasRotation = hasRotationInsight(
              song.insights as SongInsightsData
            );
            const hasRegions = hasRegionsInsight(
              song.insights as SongInsightsData
            );
            const hasGenres = hasGenresInsight(
              song.insights as SongInsightsData
            );

            const songWithFlags: SongLibrary = {
              ...song,
              insightsFlags: {
                hasPeakAirplayMorningInsight: hasMorning,
                hasPeakAirplayEveningInsight: hasEvening,
                hasPeakAirplayNightInsight: hasNight,
                hasMomentumGainInsight: hasMomentumGain,
                hasMomentumStagnantInsight: hasMomentumStagnant,
                hasMomentumDownInsight: hasMomentumDown,
                hasRotationInsight: hasRotation,
                hasRegionsInsight: hasRegions,
                hasGenresInsight: hasGenres,
              },
            };

            if (hasMorning) {
              insightCounts.peakAirplayMorningInsights += 1;
              insightLists.peakAirplayMorningInsights.push(songWithFlags);
            }
            if (hasEvening) {
              insightCounts.peakAirplayEveningInsights += 1;
              insightLists.peakAirplayEveningInsights.push(songWithFlags);
            }
            if (hasNight) {
              insightCounts.peakAirplayNightInsights += 1;
              insightLists.peakAirplayNightInsights.push(songWithFlags);
            }
            if (hasMomentumGain) {
              insightCounts.momentumInsightsGain += 1;
              insightLists.momentumInsightsGain.push(songWithFlags);
            } else if (hasMomentumStagnant) {
              insightCounts.momentumInsightsStagnant += 1;
              insightLists.momentumInsightsStagnant.push(songWithFlags);
            } else if (hasMomentumDown) {
              insightCounts.momentumInsightsDown += 1;
              insightLists.momentumInsightsDown.push(songWithFlags);
            }
            if (hasRotation) {
              insightCounts.rotationInsights += 1;
              insightLists.rotationInsights.push(songWithFlags);
            }
            if (hasRegions) {
              insightCounts.regionsInsights += 1;
              const regionNames =
                song.insights?.top_regions
                  ?.map((region) => region.name)
                  .filter(Boolean) ?? [];
              const uniqueRegionNames = Array.from(new Set(regionNames));

              uniqueRegionNames.forEach((regionName) => {
                const existingSongs =
                  insightLists.regionsInsights[regionName] ?? [];
                insightLists.regionsInsights[regionName] = [
                  ...existingSongs,
                  songWithFlags,
                ];
              });
            }
            if (hasGenres) {
              insightCounts.genresInsights += 1;
              const genreNames =
                song.insights?.top_genres
                  ?.map((genre) => genre.name)
                  .filter(Boolean) ?? [];

              const uniqueGenreNames = Array.from(new Set(genreNames));

              uniqueGenreNames.forEach((genreName) => {
                const existingSongs =
                  insightLists.genresInsights[genreName] ?? [];
                insightLists.genresInsights[genreName] = [
                  ...existingSongs,
                  songWithFlags,
                ];
              });
            }

            return songWithFlags;
          }
        );

        state.isFetching = false;
        state.songsLibrary = songsWithInsightFlags;
        state.songsLibraryInsights = insightCounts;
        state.songsLibraryInsightLists = insightLists;
        return state;
      })
      .addCase(songsLibraryOverview.rejected, (state) => {
        state.isFetching = false;
        state.hasError = true;
        Sentry.captureException(
          formatTextForSentryLog('failed to get Songs Library Overview')
        );
      }),
  reducers: {
    saveTableStatusDesktop: (state, action) => {
      state.tableStatus = {
        sortingId: action.payload.id,
        sortingOrder: action.payload.order,
        songSortSelected: false,
      };
    },
    saveTableStatusMobile: (state, action) => {
      if (action.payload.id != null) {
        state.tableStatus = {
          sortingId: action.payload.id,
          sortingOrder: action.payload.order,
          songSortSelected: action.payload.songSortSelected,
        };
      } else {
        state.tableStatus.songSortSelected = action.payload;
      }
    },
    clearSongLibrary: (state) => {
      state.songsLibrary = [];
    },
  },
});

export const {
  saveTableStatusDesktop,
  saveTableStatusMobile,
  clearSongLibrary,
} = Library.actions;

export default Library.reducer;
